import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  CarouselContainer,
  CarouselImage,
  NavigationButton,
  IndicatorsContainer,
  Indicator,
  CarouselWrapper,
  CarouselSlide,
} from './styled.js';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { resolveImageUrl } from 'utils/apiOrigin.js';

const Carousel = ({
  items,
  autoPlay = false,
  interval = 10000,
  fit = 'cover', // NOVO: controle de object-fit
  aspectRatio = '16/9', // NOVO: controle do aspect-ratio do container
  bg, // NOVO: fundo do container (ex.: '#fff')
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const timerRef = useRef(null);
  const total = Array.isArray(items) ? items.length : 0;

  const prevSlide = () => setCurrentIndex((i) => (i === 0 ? total - 1 : i - 1));
  const nextSlide = () => setCurrentIndex((i) => (i === total - 1 ? 0 : i + 1));

  useEffect(() => {
    if (!autoPlay || total < 2) return;
    timerRef.current = setInterval(nextSlide, interval);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [autoPlay, interval, total]);

  const isAbsoluteOrSpecial = (link) =>
    /^https?:\/\//i.test(link) ||
    link.startsWith('data:') ||
    link.startsWith('blob:') ||
    link.startsWith('//');

  const isFrontAsset = (link) =>
    link.startsWith('/assets/') || // public/assets
    link.startsWith('/static/media/'); // assets gerados pelo bundler (CRA/Webpack)

  const isBackendStatic = (link) =>
    link.startsWith('/static/') && !link.startsWith('/static/media/'); // backend

  const getImageSrc = (link) => {
    if (!link) return '';
    if (typeof link !== 'string') return link; // import via bundler já vem resolvido
    if (isAbsoluteOrSpecial(link) || isFrontAsset(link)) return link;
    if (isBackendStatic(link)) return resolveImageUrl(link);
    // Qualquer outro path relativo/absoluto → deixar o backend resolver
    return resolveImageUrl(link);
  };

  if (!total) return null;

  return (
    <>
      <CarouselContainer $aspect={aspectRatio} $bg={bg}>
        <CarouselWrapper $currentIndex={currentIndex}>
          {items.map((item, index) => {
            const src = getImageSrc(item.imageLink);
            const img = (
              <CarouselImage src={src} alt={item.alt || `Slide ${index + 1}`} $fit={fit} />
            );
            return (
              <CarouselSlide key={index}>
                {item.link ? (
                  <a href={item.link} target="_blank" rel="noopener noreferrer">
                    {img}
                  </a>
                ) : (
                  img
                )}
              </CarouselSlide>
            );
          })}
        </CarouselWrapper>

        {total > 1 && (
          <>
            <NavigationButton $position="left" onClick={prevSlide} aria-label="Anterior">
              <ArrowBack sx={{ fontSize: '50px' }} />
            </NavigationButton>
            <NavigationButton $position="right" onClick={nextSlide} aria-label="Próximo">
              <ArrowForward sx={{ fontSize: '50px' }} />
            </NavigationButton>
          </>
        )}
      </CarouselContainer>

      {total > 1 && (
        <IndicatorsContainer>
          {items.map((_, index) => (
            <Indicator
              key={index}
              $active={index === currentIndex}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Ir para o slide ${index + 1}`}
            />
          ))}
        </IndicatorsContainer>
      )}
    </>
  );
};

Carousel.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      imageLink: PropTypes.string.isRequired,
      link: PropTypes.string,
      alt: PropTypes.string,
    }),
  ).isRequired,
  autoPlay: PropTypes.bool,
  interval: PropTypes.number,
  fit: PropTypes.oneOf(['cover', 'contain', 'fill', 'none', 'scale-down']),
  aspectRatio: PropTypes.string,
  bg: PropTypes.string,
};

export default Carousel;
